import React, {
  forwardRef,
  Fragment,
  useEffect,
  useImperativeHandle,
  useState,
} from "react";
import { PropTypes } from "prop-types";

import {
  Dialog,
  Grid,
  IconButton,
  Typography,
  DialogActions,
  DialogContent,
  DialogTitle,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

import {
  usePreviousValue,
  useDidMountEffect,
  isBoolean,
} from "../../shared/utils/utils";

function _CustomModal(
  {
    title,
    size,
    getOpenButton,
    getFooter,
    isOpen = null,
    setIsOpen = null,
    onToggle,
    onClose,
    zIndex = 7500,
    children,
  },
  ref,
) {
  const [_isOpen, _setIsOpen] = useState(setIsOpen ? isOpen : false);
  const prevIsOpen = usePreviousValue(_isOpen);

  const toggle = () => {
    toggleTo();
  };

  const toggleTo = (newVal = null) => {
    if (setIsOpen) setIsOpen(newVal !== null ? newVal : !isOpen);
    else _setIsOpen(newVal !== null ? newVal : !_isOpen);
  };

  // Expose the function to the parent using `useImperativeHandle`
  useImperativeHandle(ref, () => ({
    toggle: toggleTo,
    isOpen: _isOpen,
  }));

  useEffect(() => {
    if (setIsOpen && _isOpen !== isOpen) _setIsOpen(isOpen);
  }, [isOpen]);

  useDidMountEffect(() => {
    if (isBoolean(prevIsOpen) && isBoolean(_isOpen) && onToggle)
      onToggle(_isOpen);
  }, [_isOpen]);

  // fullscreen if display size = xs
  const theme = useTheme();
  const xsScreen = useMediaQuery(theme.breakpoints.down("md"));
  const fullScreen = size === "fullscreen" || xsScreen;

  const fixedSize = !fullScreen && size !== "auto";

  return (
    <Fragment>
      {getOpenButton ? getOpenButton(toggle) : null}
      <Dialog
        style={{
          zIndex, // in front,
        }}
        open={_isOpen}
        onClose={() => {
          if (onClose) onClose(toggle);
          else toggle();
        }}
        fullScreen={fullScreen}
        fullWidth={fixedSize}
        maxWidth={fixedSize ? size : undefined}
        aria-labelledby="draggable-dialog-title"
      >
        <DialogTitle sx={{ paddingY: "5px", backgroundColor: "tertiary.main" }}>
          <Grid container>
            <Grid xs={10} sm={10} md={10} item justifyContent="flex-start">
              <Typography inline="true" fontSize="h5.fontSize" color="primary">
                {title}
              </Typography>
            </Grid>
            <Grid
              xs={2}
              sm={2}
              md={2}
              item
              justifyContent="flex-end"
              display="grid"
            >
              <IconButton
                onClick={() => {
                  if (onClose) onClose(toggle);
                  else toggle();
                }}
                aria-label="close"
              >
                <CloseIcon color="primary" />
              </IconButton>
            </Grid>
          </Grid>
        </DialogTitle>
        {children ? (
          <DialogContent sx={{ backgroundColor: "background.main" }}>
            {children}
          </DialogContent>
        ) : null}
        {getFooter ? (
          <DialogActions
            sx={{ paddingY: "5px", backgroundColor: "tertiary.main" }}
          >
            {getFooter(toggle)}
          </DialogActions>
        ) : null}
      </Dialog>
    </Fragment>
  );
}

// wrap into forwardref
const CustomModal = forwardRef(_CustomModal);
export default CustomModal;

_CustomModal.propTypes = {
  title: PropTypes.string,
  size: PropTypes.string,
  getOpenButton: PropTypes.func,
  getFooter: PropTypes.func,
  isOpen: PropTypes.bool,
  setIsOpen: PropTypes.func,
  onToggle: PropTypes.func,
  onClose: PropTypes.func,
  children: PropTypes.node,
  zIndex: PropTypes.number,
};
